import { Divider } from "antd"
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronRight,
  Copy,
  FileText,
  InfoIcon
} from "lucide-react"

function mapOutline(raw: any[]) {
  return raw.map((c: any, i: number) => ({
    id: c.id || `card-${i}-${Date.now()}`,
    title: c.title || `Slide ${i + 1}`,
    type: c.type || "content",
    layout: c.layout,
    bullets: Array.isArray(c.bullets) ? c.bullets.filter(Boolean) : [],
    userNotes: c.user_notes || c.userNotes || "",
    data_source: c.data_source,
    search_keywords: c.search_keywords,
    account_ids: c.account_ids
      ? c.account_ids
      : c.account_id
        ? [c.account_id]
        : undefined
  }))
}
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import useParentData from "~/hooks/useParentData"

import AgentQuoteSelection from "~/components/agent-quote-selection"
import RichTextEditor from "~/components/base-editor/RichTextEditor"
import { EVENTS } from "~/containers/os-agents/constants"
import { useGetAgentTools, useGetKosTools } from "~/hooks/os.agents"
import { useCopyToClipboardFormatted } from "~/hooks/useClipboard"
import useGlobalStore from "~/store/global"
import useOsAgentsIdStore from "~/store/os.agents_.$id"
import PptSessionLinkCard from "~/components/ppt-session-link-card"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { getFileTypeAndIcon, removeDuplicates } from "~/utils/helpers"
import {
  parseQuotedMessage,
  stripInternalAccountId
} from "~/utils/parse-quoted-message"
import {
  SuggestionItem,
  getSuggestionLabel,
  getSuggestionPrompt
} from "~/types/knowledge-os"

import AgentSelectAccount from "./agent-select-account"
import AgentSessionHistory from "./agent-session-history"
import ThoughtTimer from "./components/thought-timer"

export default function AgentIdMessages({
  sendMessageRef,
  sessionIdOverride,
  embedded = false
}: {
  sendMessageRef: any
  sessionIdOverride?: string
  embedded?: boolean
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { data: agentToolsData } = useGetAgentTools()
  const { data: kosToolsData } = useGetKosTools()
  const agentToolsList = agentToolsData?.data ?? []
  const kosToolsList = kosToolsData?.data ?? []
  const toolsList = [...agentToolsList, ...kosToolsList]
  const { id: routeSessionId = "" } = useParams()
  const existingSessionId = sessionIdOverride ?? routeSessionId
  const messages = useOsAgentsIdStore(
    (store) => store.messages?.[existingSessionId]
  )
  useEffect(() => {
    if (messages && Object.keys(messages).length) {
      messagesEndRef.current?.scrollIntoView()
    }
  }, [messages])

  // Persisted history rendered above us by <AgentSessionHistory>. Same
  // hook + key as agent-session-history.tsx — both subscribe to the parent
  // route loader so we share one data source.
  const historyResponse = useParentData<{
    id: string
    history: any[]
  }>(`/os/agents/${existingSessionId}`, "", "")
  const history =
    historyResponse?.id === existingSessionId
      ? (historyResponse?.history ?? [])
      : []

  const rawMessagesList = messages && Object.values(messages)
  // Dedupe: <AgentSessionHistory> already renders persisted turns above
  // us. Drop any in-memory store entry whose role/content already appears
  // in history so the same user query / assistant reply doesn't render
  // twice. Bidirectional substring match handles V1's quote-prefix case
  // (history stores the embedded preamble, store stores just the user
  // input). Empty / pending content always renders — that's the live
  // streaming bubble that history hasn't caught up with yet.
  const messagesList = useMemo(() => {
    if (!rawMessagesList?.length) return rawMessagesList
    if (!history.length) return rawMessagesList
    return rawMessagesList.filter((m: any) => {
      const role: "USER" | "ASSISTANT" =
        m?.role === "user" ? "USER" : "ASSISTANT"
      let content = ""
      if (role === "USER") {
        content =
          typeof m.content === "string"
            ? m.content
            : Array.isArray(m.content)
              ? m.content[0] || ""
              : ""
      } else {
        content = m.content?.parts?.[0] || m.content?.resourceParams?.text || ""
      }
      const trimmed = content.trim()
      if (!trimmed) return true
      return !history.some((h: any) => {
        if (h?.actor !== role) return false
        const text = typeof h?.text === "string" ? h.text.trim() : ""
        if (!text) return false
        return text.includes(trimmed) || trimmed.includes(text)
      })
    })
  }, [rawMessagesList, history])
  return (
    <div className="overflow-auto flex-1">
      <div
        className={`flex-1 flex flex-col gap-4 pt-6 pb-4 max-w-4xl mx-auto ${existingSessionId ? "px-4" : "px-10"}`}
      >
        {!embedded && (
          <AgentSessionHistory
            sendMessageRef={sendMessageRef}
            hasNewMessage={messagesList?.length > 0}
          />
        )}
        {messagesList?.map((item: any, index: number) => (
          <MessageItem
            key={item.id}
            item={item}
            toolsList={toolsList}
            sendMessageRef={sendMessageRef}
            isLast={index === messagesList?.length - 1}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}

const MessageItem = ({
  item,
  toolsList,
  sendMessageRef,
  isLast
}: {
  item: any
  toolsList: any
  sendMessageRef: any
  isLast: boolean
}) => {
  const { t } = useTranslation()
  const setSourcesDrawerData = useOsAgentsIdStore(
    (store) => store.setSourcesDrawerData
  )
  const setQuotedText = useOsAgentsIdStore((store) => store.setQuotedText)
  const pendingMessage = useOsAgentsIdStore((store) => store.pendingMessage)
  const [copiedText, copy] = useCopyToClipboardFormatted()
  const isUser = item.role === "user"
  const contentType = item.content_type
  const isToolMessage = contentType === "tool"
  const isUiComponentMessage =
    contentType === "ui_component" && item.content.resourceName
  const isAccountSelectionTool = contentType === "account_selection"
  const isPending = item.isPending
  const errorMessage = item.error

  // Use llmVerifiedSources from history API or source from real-time messages
  const sources = item.llmVerifiedSources || item.source || []
  const uniqueSources = removeDuplicates(sources, "name")

  let content = ""
  if (isUser) {
    content = typeof item.content === "string" ? item.content : item.content[0]
    content = content.replace(/\s*\(ppt_session_id:\s*[a-zA-Z0-9\-_]+\)/g, "")
  } else {
    if (isToolMessage) {
      content = item.content.resourceParams?.text || ""
    } else {
      content = item.content?.parts ? item.content.parts[0] : ""
    }
  }

  // For user messages, strip the embedded quote preamble (KOS/RFPOS format)
  // so the bubble shows just the user's question — the excerpt is rendered
  // separately above the text as a left-aligned italic block.
  const parsedUserMessage = isUser ? parseQuotedMessage(content) : null
  const userQuoteExcerpt = parsedUserMessage?.contextQuote ?? null
  const displayContent = isUser
    ? stripInternalAccountId(
        userQuoteExcerpt ? parsedUserMessage!.userMessage : content
      )
    : content

  // Detect PPT session links in assistant messages
  const pptLinkMatch =
    !isUser && content
      ? content.match(
          /\[Open Presentation:\s*(.+?)\]\((\/k-os\/presentation-builder\?session=[^)]+)\)/
        )
      : null
  const pptTitle = pptLinkMatch?.[1] || ""
  const pptUrl = pptLinkMatch?.[2] || ""

  if (errorMessage && !isUser) {
    return (
      <div className="flex items-center gap-2">
        <div className="border border-primary dark:bg-dark-3 p-1 rounded-full flex-shrink-0">
          <img src="/favicon.png" alt="" className="w-5 h-5" />
        </div>
        <div className="border border-red-400 dark:border-red-300 rounded-xl px-3 py-[6px] flex items-center gap-2 w-fit text-xs text-red-400 dark:text-red-300">
          <InfoIcon size={16} />
          {errorMessage ||
            "We’ve encountered an issue, and our team is currently looking into it. Please contact at support@orbitshift.ai if the issue persists"}
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className={`flex flex-col gap-2 relative ${
          isUser ? "w-fit max-w-[70%] ml-auto" : "max-w-[80%]"
        }`}
      >
        {item.files?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {item.files.map((file: File) => {
              const fileType = getFileTypeAndIcon(file.name)
              return (
                <div
                  key={file.name}
                  className="flex items-start gap-2 bg-white dark:bg-dark-2 border border-light-border dark:border-border px-2 py-[6px] rounded-lg"
                >
                  <div className={`p-2 rounded-md ${fileType.color}`}>
                    <FileText size={16} className="text-dark-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-dark-2 dark:text-dark-6 text-ellipsis max-w-24 overflow-hidden whitespace-nowrap">
                      {file.name}
                    </span>
                    <span className="text-xs text-dark-2 dark:text-dark-6">
                      {fileType.type}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {!isUser && <ThoughtTimer isPending={isPending} messageId={item.id} />}
        {isUser && userQuoteExcerpt && (
          <div className="flex items-start gap-1.5 text-xs text-dark-2/60 dark:text-dark-6/60 border-l-2 border-primary/40 pl-2 self-stretch">
            <span className="text-primary/60 flex-shrink-0">↳</span>
            <p className="line-clamp-2 italic m-0 text-left flex-1">
              {userQuoteExcerpt}
            </p>
          </div>
        )}
        {displayContent &&
          (!isUser ? (
            <AgentQuoteSelection
              onQuote={setQuotedText}
              disabled={isPending || item?.status === "IN_PROGRESS"}
            >
              <RichTextEditor
                hideToolBar
                className="p-0 p-13 text-dark-2 dark:text-dark-6 flex-1 justify-start !pl-11"
                content={displayContent}
                contentClassName="transparent-body"
              />
            </AgentQuoteSelection>
          ) : (
            <RichTextEditor
              hideToolBar
              className="p-0 p-13 text-dark-2 dark:text-dark-6 flex justify-end px-4 py-[10px] bg-genesis dark:bg-dark-2 rounded-[10px]"
              content={displayContent}
              contentClassName="transparent-body"
            />
          ))}
        {errorMessage && !isUser && (
          <div className="border border-red-400 dark:border-red-300 rounded-xl px-3 py-[6px] flex items-center gap-2 w-fit text-xs text-red-400 dark:text-red-300">
            <InfoIcon size={16} />
            {errorMessage ||
              "We’ve encountered an issue, and our team is currently looking into it. Please contact at support@orbitshift.ai if the issue persists"}
          </div>
        )}
        {isAccountSelectionTool && (
          <div className="max-w-2xl pl-11">
            <AgentSelectAccount
              label={t("os_agents.placeholder_select_an_account")}
              sendMessageRef={sendMessageRef}
            />
          </div>
        )}
        {isUiComponentMessage && (
          <ToolName
            toolsList={toolsList}
            resourceName={item.content.resourceName}
            content={item.content}
            sendMessageRef={sendMessageRef}
          />
        )}

        {pptUrl && (
          <div className="pl-11 pt-2">
            <PptSessionLinkCard title={pptTitle} sessionUrl={pptUrl} />
          </div>
        )}
        {uniqueSources && uniqueSources.length > 0 && (
          <div className="flex flex-row gap-2 border-t border-light-border dark:border-border pt-4 ml-11 items-center">
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-dark-2 border border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-dark-1 transition-colors"
              onClick={() => {
                track("source_clicked", {
                  source_name: "View all sources",
                  sources_available: uniqueSources.map(
                    (source: any) => source.name
                  ),
                  triggered_from: "os_agents"
                })
                setSourcesDrawerData(uniqueSources)
              }}
            >
              <span className="text-xs font-medium text-dark-2 dark:text-dark-6">
                {t("label_sources")}
              </span>
              <ArrowDown className="w-3 h-3 text-dark-2 dark:text-dark-6 rotate-[-90deg]" />
            </button>
          </div>
        )}
        {item.score?.score_category && item.score?.score_category !== "NA" && (
          <p className="rounded-lg text-xs bg-genesis dark:bg-dark-1 text-primary dark:text-blue-300 border-none mt-1">
            Confidence:{" "}
            <span className="font-semibold">
              {item.score.score_category.replace(/Confidence/gi, "")}
            </span>
          </p>
        )}
        {!isUser && !isPending && content && (
          <div className="mt-4 border-t border-light-border dark:border-border flex items-center pt-2 ml-11">
            <button
              type="button"
              onClick={() => copy(content)}
              aria-label={copiedText ? "Copied" : "Copy"}
              className={`
                flex items-center gap-2 px-2 py-1 cursor-pointer
                font-small text-xs
                text-dark-2 opacity-70 dark:text-dark-6 hover:opacity-90
                transition-colors mr-auto
                ${copiedText ? "text-grey-100 dark:text-dark-6" : ""}
              `}
            >
              {copiedText ? (
                <>
                  <Check className="w-4 h-4 text-dark-2 dark:text-dark-6" />
                  {t("label_copied")}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 text-dark-2 dark:text-dark-6" />{" "}
                  {t("label_copy")}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      {item.suggestions && item.suggestions.length > 0 && (
        <>
          <div className="flex flex-col gap-2 items-end">
            <h3 className="text-xs tracking-wide text-gray-600 dark:text-gray-400">
              {t("label_suggested_actions")}
            </h3>
            {item.suggestions.map(
              (suggestion: SuggestionItem, index: number) => (
                <button
                  key={index}
                  className="w-fit text-left flex items-center gap-2 text-xs text-dark-2 dark:text-dark-6 border border-secondary bg-white dark:bg-dark-3 px-3 py-1 rounded-xl"
                  onClick={() => {
                    track(EVENTS.SUGGESTION_CLICKED, {
                      ...getBaseEventProps({ feature: "os_agents" }),
                      suggestion_label: getSuggestionLabel(suggestion)
                    })
                    sendMessageRef.current?.({
                      tool_id: "",
                      agentQuery: getSuggestionPrompt(suggestion),
                      displayText: getSuggestionLabel(suggestion)
                    })
                  }}
                  disabled={!!pendingMessage}
                >
                  {getSuggestionLabel(suggestion)}
                  <div
                    className={`bg-primary dark:text-primary-light flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg`}
                  >
                    <ArrowUp size={12} className="text-dark-6" />
                  </div>
                </button>
              )
            )}
          </div>
        </>
      )}
      {!isUser &&
        item.suggestions &&
        item.suggestions.length > 0 &&
        !isLast && <Divider className="m-0" />}
    </>
  )
}

export const ToolName = ({
  toolsList,
  resourceName,
  content
}: {
  toolsList: any[]
  resourceName: string
  content: any
  sendMessageRef?: any
}) => {
  const { t } = useTranslation()
  const tool = toolsList?.find((tool) => tool.id === resourceName)
  const showSplitter = useOsAgentsIdStore((store) => store.showSplitter)
  const setShowSplitter = useOsAgentsIdStore((store) => store.setShowSplitter)
  const pptOutlineState = useOsAgentsIdStore((store) => store.pptOutlineState)
  const isPptAgent =
    resourceName === "GeneratePresentationBuilderAgent" ||
    resourceName === "PresentationOutlineBuilder"
  const pptSessionId = isPptAgent ? content?.resourceParams?.session_id : null
  const pptCards = pptOutlineState.cards.length
  const pptSlides = (pptOutlineState.slides as any[])?.length || 0
  const pptTarget = pptOutlineState.requestedCount
  const pptLabel = isPptAgent
    ? !pptOutlineState.hasStarted
      ? pptSessionId
        ? "Loading..."
        : "Presentation"
      : pptOutlineState.slidesStatus === "ready"
        ? `Slides Ready (${pptSlides})`
        : pptOutlineState.slidesStatus === "generating"
          ? `Generating Slides... ${pptSlides}/${pptCards || pptTarget}`
          : pptCards > 0 && pptCards < pptTarget
            ? `Generating Outline... ${pptCards}/${pptTarget}`
            : pptOutlineState.isStreaming
              ? `Generating Outline... ${pptCards}/${pptTarget}`
              : pptCards > 0
                ? `Outline Ready (${pptCards})`
                : "Presentation"
    : null
  // Restore PPT state from backend on mount
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  useEffect(() => {
    if (
      !isPptAgent ||
      pptOutlineState.hasStarted ||
      !pptSessionId ||
      !tokenInfo
    )
      return
    import("~/api/ppt-ai-generator").then(({ fetchPptSession }) => {
      fetchPptSession(tokenInfo, pptSessionId)
        .then((res: any) => {
          const session = res?.data?.data || res?.data
          if (!session) return
          const setPptState = useOsAgentsIdStore.getState().setPptOutlineState
          const requestedCount = session.messages?.[0]?.slide_count || 8
          if (session.slides && session.slides.length > 0) {
            const outlineCount = session.outline?.length || requestedCount
            const slidesComplete = session.slides.length >= outlineCount
            const isIterating = session.status === "iterating"
            setPptState({
              hasStarted: true,
              sessionId: pptSessionId,
              slidesStatus: isIterating
                ? "generating"
                : slidesComplete
                  ? "ready"
                  : "generating",
              cards: mapOutline(session.outline || []),
              slides: session.slides,
              requestedCount
            })
            if (isIterating) {
              const setShowSplitter =
                useOsAgentsIdStore.getState().setShowSplitter
              setShowSplitter({
                resourceName: "PresentationOutlineBuilder",
                resourceParams: {
                  session_id: pptSessionId,
                  status: "slides_updated"
                }
              })
            }
          } else if (session.outline && session.outline.length > 0) {
            const isIncomplete = session.outline.length < requestedCount
            setPptState({
              hasStarted: true,
              sessionId: pptSessionId,
              cards: mapOutline(session.outline),
              requestedCount,
              isStreaming: isIncomplete
            })
          }
        })
        .catch((_) => {
          void _
        })
    })
  }, [isPptAgent, pptOutlineState.hasStarted, pptSessionId, tokenInfo])

  return (
    <>
      <button
        className="border border-primary dark:border-gray-400 rounded-lg min-w-52 max-w-60 w-fit overflow-hidden ml-11"
        onClick={() => {
          const isContentUpdated =
            JSON.stringify(showSplitter) !== JSON.stringify(content)
          if (isContentUpdated && isPptAgent && pptSessionId && tokenInfo) {
            import("~/api/ppt-ai-generator").then(({ fetchPptSession }) => {
              fetchPptSession(tokenInfo, pptSessionId)
                .then((res: any) => {
                  const session = res?.data?.data || res?.data
                  if (!session) return
                  const setPptState =
                    useOsAgentsIdStore.getState().setPptOutlineState
                  const requestedCount = session.messages?.[0]?.slide_count || 8
                  if (session.slides && session.slides.length > 0) {
                    const outlineCount =
                      session.outline?.length || requestedCount
                    const slidesComplete = session.slides.length >= outlineCount
                    const isIterating = session.status === "iterating"
                    setPptState({
                      slidesStatus: isIterating
                        ? "generating"
                        : slidesComplete
                          ? "ready"
                          : "generating",
                      cards: mapOutline(session.outline || []),
                      slides: session.slides,
                      requestedCount,
                      isStreaming: false
                    })
                  } else if (session.outline) {
                    const isIncomplete = session.outline.length < requestedCount
                    setPptState({
                      cards: mapOutline(session.outline),
                      requestedCount,
                      isStreaming: isIncomplete
                    })
                  }
                })
                .catch((_) => {
                  void _
                })
            })
          }
          setShowSplitter(isContentUpdated ? content : null)
          track(EVENTS.MESSAGE_CARD_CLICKED, {
            ...getBaseEventProps({ feature: "os_agents" }),
            tool_id: resourceName
          })
        }}
      >
        <div className="px-3 py-2 bg-genesis dark:bg-dark-2 flex items-center gap-3 text-left">
          <FileText size={20} className="flex-shrink-0" />
          <p className="text-xs">
            {pptLabel ||
              (resourceName === "SalesPitchDeckBuilder"
                ? "Sales Pitch Deck"
                : null) ||
              tool?.name ||
              "Fetching name..."}
          </p>
        </div>
        <div className="px-3 py-2 flex justify-between items-center gap-3">
          <p className="text-xs">{t("label_preview")}</p>
          <ChevronRight size={16} />
        </div>
      </button>
      {/* Generate Slides button hidden — not functional yet, will be re-introduced later */}
    </>
  )
}
